/* Strip plot, porte de `strip()` / `renderAxis()` (legacy:954, :994).
 *
 * Uma linha por categoria: caixa p25–p75, tick na mediana, um ponto por usuario.
 * Os pontos sao empacotados em faixas para nao se sobreporem — sem isso, dez
 * usuarios com valores proximos viram um ponto so e a dispersao some.
 *
 * Os quantis vem do servidor, com a mesma interpolacao linear do arquivo
 * original: as bordas da caixa sao posicao em pixel, e outra convencao de
 * quantil desloca a caixa visivelmente contra a referencia congelada. */

import { flowColor, shortName } from "../lib/format";
import type { DistributionRow } from "../lib/api";

const ROW_H = 26, DOT_R = 3, LANE_H = 7;

export interface StripPlotProps {
  rows: DistributionRow[];
  width: number;
  logScale: boolean;
  onPick?: (userId: string) => void;
  /** Hover da LINHA: o que se mostra é o agregado, nunca o ponto de um usuário. */
  onHoverRow?: (info: { row: DistributionRow; x: number; y: number } | null) => void;
}

function makeScale(rows: DistributionRow[], width: number, logScale: boolean) {
  const all = rows.flatMap((r) => r.points.map((p) => p.value)).filter((v) => v > 0);
  const max = Math.max(...all, 1);
  const min = Math.max(Math.min(...all, max), 0.01);
  const left = 0, right = width;
  if (!logScale) {
    return (v: number) => left + (right - left) * (Math.max(v, 0) / max);
  }
  const lo = Math.log10(min), hi = Math.log10(max);
  return (v: number) =>
    v <= 0 ? left : left + (right - left) * ((Math.log10(v) - lo) / (hi - lo || 1));
}

export function StripPlot({ rows, width, logScale, onPick, onHoverRow }: StripPlotProps) {
  if (!rows.length) return null;
  const scale = makeScale(rows, width, logScale);
  const height = rows.length * ROW_H + 8;

  const hover = (row: DistributionRow) => ({
    onPointerMove: (event: React.PointerEvent) =>
      onHoverRow?.({ row, x: event.clientX, y: event.clientY }),
    onPointerLeave: () => onHoverRow?.(null),
  });

  return (
    <svg viewBox={`0 0 ${width} ${height}`} width="100%" height={height}
         role="img"
         aria-label={`Distribuição entre usuários de ${rows.length} categorias, com caixa do primeiro ao terceiro quartil e um ponto por usuário`}
         style={{ display: "block", overflow: "visible" }}>
      {rows.map((row, index) => {
        const y = index * ROW_H + ROW_H / 2;
        const color = flowColor(row.flow);

        // Empacotamento em faixas: pontos que colidem descem uma faixa.
        const lanes: number[] = [];
        const placed = row.points.map((p) => {
          const x = scale(p.value);
          let lane = 0;
          while (lanes[lane] !== undefined && Math.abs(lanes[lane] - x) < DOT_R * 2) lane++;
          lanes[lane] = x;
          return { ...p, x, lane: lane % 3 };
        });

        return (
          <g key={row.key} {...hover(row)}>
            {/* Alvo de hover da linha inteira: o tooltip fala do agregado. */}
            <rect x={0} y={y - ROW_H / 2} width={width} height={ROW_H}
                  fill="transparent" style={{ pointerEvents: "all" }} />
            <line x1={0} y1={y} x2={width} y2={y} stroke="var(--grid)" strokeWidth="1" />
            <rect x={scale(row.p25)} y={y - 7} width={Math.max(1, scale(row.p75) - scale(row.p25))}
                  height={14} fill={color} opacity="0.13" rx="2" />
            <line x1={scale(row.median)} y1={y - 8} x2={scale(row.median)} y2={y + 8}
                  stroke={color} strokeWidth="2" />
            {/* Média, tracejada ao lado da mediana: a distância entre as duas é a
                assimetria da distribuição, que a caixa sozinha não mostra. */}
            <line x1={scale(row.mean)} y1={y - 8} x2={scale(row.mean)} y2={y + 8}
                  stroke={color} strokeWidth="1.4" strokeDasharray="2 2" opacity="0.85" />
            {placed.map((p) => (
              <circle
                key={p.user_id}
                cx={p.x}
                cy={y + (p.lane - 1) * (LANE_H / 2)}
                r={DOT_R}
                fill={color}
                opacity="0.62"
                style={{ cursor: onPick ? "pointer" : undefined }}
                onClick={() => onPick?.(p.user_id)}
              />
            ))}
          </g>
        );
      })}
    </svg>
  );
}

/** Rotulos das linhas, fora do SVG para poderem quebrar e serem selecionaveis. */
export function StripLabels({ rows, onHoverRow }: {
  rows: DistributionRow[];
  onHoverRow?: StripPlotProps["onHoverRow"];
}) {
  return (
    <div style={{ paddingTop: 4 }}>
      {rows.map((row) => (
        <div key={row.key}
             onPointerMove={(e) => onHoverRow?.({ row, x: e.clientX, y: e.clientY })}
             onPointerLeave={() => onHoverRow?.(null)}
             style={{ height: ROW_H, display: "flex", alignItems: "center", gap: 7,
                      fontSize: 12.5, whiteSpace: "nowrap" }}>
          <span style={{ width: 7, height: 7, borderRadius: 2, flex: "0 0 auto",
                         background: flowColor(row.flow) }} />
          <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}
                title={row.key}>{shortName(row.key)}</span>
          <span className="mono" style={{ marginLeft: "auto", color: "var(--ink-3)",
                                          fontSize: 11.5 }}>
            {row.n}/{row.total_users}
          </span>
        </div>
      ))}
    </div>
  );
}
